import { createContext, useCallback, useContext, useEffect, useState } from "react";

import { ApiRequests, ApiResponses, Successful } from "../data/api-endpoints";
import logger from "../log";

const debug = logger("api:cache");

const ApiCacheContext = createContext({
  forgetCache<T extends keyof ApiRequests>(key: T, data?: ApiRequests[T], immediate = false) {},
  getCache<T extends keyof ApiRequests>(
    key: T,
    data: ApiRequests[T] | undefined,
  ): ["cleared" | "fresh" | "stale", Successful<ApiResponses[T]>] | ["miss", undefined] {
    throw new Error("Cache not initialized");
    return ["miss", undefined];
  },
  interactedWithOldVersion: false,
  invalidateCache(cutoff: Date, ids: string[]) {},
  newVersionAvailable: false,
  setCache<T extends keyof ApiRequests>(key: T, data: ApiRequests[T] | undefined, value: any) {},
});

export function makeNewerApiVersionInteractionEvent() {
  return new CustomEvent("api_version:newer");
}

export function makeOlderApiVersionInteractionEvent() {
  return new CustomEvent("api_version:older");
}

export function useApiCache() {
  return useContext(ApiCacheContext);
}

const EIGHT_HOURS = 1000 * 60 * 60 * 8;
const DATA_TTL = process.env.NODE_ENV === "development" ? 10_000 : 60_000;

export default function ApiCacheProvider({ children }: { children: JSX.Element }): JSX.Element {
  const [apiCache, setApiCache] = useState(new Map<string, Map<string, [Date, any]>>());
  const [focusInvalidation, setFocusInvalidation] = useState(Date.now());
  const [newVersionAvailable, setNewVersionAvailable] = useState<boolean>(false);
  const [interactedWithOldVersion, setInteractedWithOldVersion] = useState<boolean>(false);
  const [lastActivityAt, setLastActivityAt] = useState<number>(Date.now());
  const [previousActivityAt, setPreviousActivityAt] = useState<number>(Date.now());

  useEffect(() => {
    const listener = () => {
      setFocusInvalidation(Date.now());
    };

    window.addEventListener("focus", listener);

    return () => {
      window.removeEventListener("focus", listener);
    };
  }, []);

  useEffect(() => {
    const newerListener = (event: CustomEvent) => {
      setNewVersionAvailable(true);
    };
    const olderListener = (event: CustomEvent) => {
      setInteractedWithOldVersion(true);
    };
    document.addEventListener("api_version:newer", newerListener as EventListener);
    document.addEventListener("api_version:older", olderListener as EventListener);

    return () => {
      document.removeEventListener("api_version:newer", newerListener as EventListener);
      document.removeEventListener("api_version:older", olderListener as EventListener);
    };
  }, []);

  useEffect(() => {
    const listener = () => {
      if (Date.now() - lastActivityAt < 60_000) return;

      setPreviousActivityAt(lastActivityAt);
      setLastActivityAt(Date.now());
    };

    document.addEventListener("mousemove", listener);

    return () => {
      document.removeEventListener("mousemove", listener);
    };
  }, [lastActivityAt]);

  useEffect(() => {
    if (newVersionAvailable && Date.now() - previousActivityAt > EIGHT_HOURS) window.reload();
  }, [previousActivityAt, newVersionAvailable]);

  const forgetCache = useCallback(<T extends keyof ApiRequests>(key: T, data?: ApiRequests[T], immediate = false) => {
    setApiCache((cache) => {
      let changed = false;
      const keyCache = cache.get(key);
      const dataKey = data ? JSON.stringify(data) : undefined;
      if (immediate) {
        if (dataKey) {
          changed = keyCache?.has(dataKey) ?? false;
          keyCache?.delete(dataKey);
        } else {
          changed = cache.has(key);
          cache.delete(key);
        }
      } else if (keyCache) {
        for (const [entryDataKey, [date, cachedValue]] of keyCache.entries()) {
          if (!dataKey || dataKey === entryDataKey) {
            changed = true;
            keyCache.set(entryDataKey, [new Date(0), cachedValue]);
          }
        }
      }
      return changed ? new Map(cache) : cache;
    });
  }, []);

  const setCache = useCallback(
    <T extends keyof ApiRequests>(key: T, data: ApiRequests[T] | undefined, value: Successful<ApiResponses[T]>) => {
      setApiCache((cache) => {
        const keyCache = cache.get(key) ?? new Map<string, [Date, any]>();
        keyCache.set(JSON.stringify(data ?? "-"), [new Date(), value]);
        cache.set(key, keyCache);

        if (cache.size > 100) cache.delete(cache.keys().next().value!);

        return new Map(cache);
      });
    },
    [],
  );

  // Returns [isFresh: boolean, data: ApiRequests[T] | undefined]
  const getCache = useCallback(
    <T extends keyof ApiRequests>(
      key: T,
      data: ApiRequests[T] | undefined,
    ): ["cleared" | "fresh" | "stale", Successful<ApiResponses[T]>] | ["miss", undefined] => {
      const cached = apiCache.get(key)?.get(JSON.stringify(data ?? "-"));
      if (cached) {
        const [date, value] = cached;
        if (date.getTime() === 0) return ["cleared", value];

        return [
          Math.max(focusInvalidation, new Date().getTime()) - date.getTime() < DATA_TTL ? "fresh" : "stale",
          value,
        ];
      }
      return ["miss", undefined];
    },
    [apiCache, focusInvalidation],
  );

  const invalidateCache = useCallback((cutoff: Date, ids: string[]) => {
    debug("Invalidating cache by %O", ids);
    setApiCache((apiCache) => {
      let changed = false;
      for (const endpoint of apiCache.keys()) {
        for (const [dataKey, [date, cacheValue]] of apiCache.get(endpoint)!.entries()) {
          if (date > cutoff) continue;

          const nonTeamIds = ids.filter((id) => !id.startsWith("t_"));

          if (
            ids.every((id) => valueDeepExists(cacheValue, id)) ||
            (nonTeamIds.length > 0 &&
              !keyDeepExists(cacheValue, "teamId") &&
              nonTeamIds.every((id) => valueDeepExists(cacheValue, id)))
          ) {
            debug("Invalidating cache %s / %s", endpoint, dataKey);
            const keyCache = apiCache.get(endpoint)!;
            changed = true;
            keyCache.set(dataKey, [new Date(0), cacheValue]);
          }
        }
      }
      return changed ? new Map(apiCache) : apiCache;
    });
  }, []);

  return (
    <ApiCacheContext.Provider
      value={{ forgetCache, getCache, interactedWithOldVersion, invalidateCache, newVersionAvailable, setCache }}
    >
      {children}
    </ApiCacheContext.Provider>
  );
}

const keyDeepExists = (obj: Record<string, any>, query: string): boolean =>
  Object.values(obj).some((v) => (typeof v === "object" && v !== null ? keyDeepExists(v, query) : v === query));

const valueDeepExists = (obj: Record<string, any>, query: string): boolean =>
  Object.values(obj).some((v) => (typeof v === "object" && v !== null ? valueDeepExists(v, query) : v === query));
